import * as format from '../data/format';
import moment from 'moment'; 
import { TRANSPARENT } from '../data/constants';


export function preRender(data, options) {
  data.tooltip = { enabled: options.interactive && options.showTooltips };
  const tooltip = data.tooltip;
  if (tooltip.enabled) {
    tooltip.borderWidth = 1;
    tooltip.borderColor = '#CCC';
    tooltip.shared = false;
    tooltip.style = getStyle(options);
    tooltip.split = false;    
    tooltip.shadow = false;
    tooltip.hideDelay = options.type === 'line' || options.type === 'area' ?
      500 : 5000;
    tooltip.formatter = function () {
      return formatter(data, options, this);
    };
  }

  if (__WSJ__) {
    if (options.product === 'wsj') {
      tooltip.borderRadius = 0;
      tooltip.padding = 5;
    }
  }
  return data;
}

function formatter(data, options, context) {
  // hide tooltip for transparent data
  if (context.color === TRANSPARENT) return false;

  const {
    needsCommas,
    liveMarketData,
    isPercent,
    isIntraday,
    series,
    longCategories,
    tooltips,
    type,
    categoriesAreDates,
    optimizeDataOrder,
    stacking,
    axes
  } = options;
  
  const categories = longCategories ||
    (options.categories ? options.categories[0] : null);

  const marketNotBar = liveMarketData && type !== 'bar';
  let category;
  if (marketNotBar) {
    const catIdx = series[0].data.findIndex(({ x }) => x === context.x);
    category = formatCategory(categories[catIdx]);
  }

  const { decimals, prefix, suffix } = context.series.userOptions;
  const tooltipY = getTooltipY(axes, tooltips, context.point.y);
  const isStackedPercent = (type === 'column' || type === 'area' || type === 'bar') &&
    stacking === 'percent';
  const value = marketNotBar ? 
    getValue(context.y, isPercent, prefix, suffix, decimals, needsCommas)
    :
    format.applyFull(tooltipY, tooltips, type === 'pie', true, true, false, isStackedPercent);

  if (!marketNotBar) {
    let firstYear = categories[0];
    let lastYear = categories[categories.length - 1];
    let isReverse = false;
    if (typeof firstYear === 'string' && typeof lastYear === 'string' && categoriesAreDates) {
      let firstYearSplit = firstYear.split(' ');
      let lastYearSplit = lastYear.split(' ');
      if (firstYearSplit.indexOf('p.m.') !== -1 && firstYearSplit.indexOf('a.m.') !== -1) {
        let firstYearTime = firstYearSplit[firstYearSplit.length - 2];
        let firstYearTimeSplit = firstYearTime.split(':')
        let hourFirstTimeSplit = parseInt(firstYearTimeSplit) + 12;
        hourFirstTimeSplit = hourFirstTimeSplit.toString();
        firstYearTimeSplit[0] = hourFirstTimeSplit;
        firstYearTimeSplit = firstYearTimeSplit.join(':');
        let lastYearTime = lastYearSplit[lastYearSplit.length - 2];
        let lastYearTimeSplit = lastYearTime.split(':')
        let hourLastTimeSplit = parseInt(lastYearTimeSplit) + 12;
        hourLastTimeSplit = hourLastTimeSplit.toString();
        lastYearTimeSplit[0] = hourLastTimeSplit;
        lastYearTimeSplit = lastYearTimeSplit.join(':');

        isReverse = firstYearTimeSplit > lastYearTimeSplit;
      } else {
        isReverse = findIfReverse(categories)
      }
    }
    
    if (isReverse) {
      categories.reverse();
    }
    const seriesData = !data.series ? series : data.series;
    var dataPoint = getDataPoint(seriesData, context, isReverse);
  }
  const index = dataPoint ? dataPoint.index : null;

  if (type === 'pie' && optimizeDataOrder && dataPoint.point.hasOwnProperty('originalIndex')) {
    category = context.point.name;
  } else if (!marketNotBar) {
    category = categories ? categories[index] : (context.point.category ||
      context.point.options.name);
  }
  const formatter = { category, value, index };

  if (isStackedPercent) {
    return formatStackedPercent(data, options, context, formatter);
  }
  return formatStandard(options, context, formatter);
}

function formatCategory(category) {
  const month = category.match(/([A-za-z]{3,})(\s|.)/g);
  const day = category.match(/(\d{1,2}),/g);
  const year = category.match(/\d{4}/g);
  const time = category.match(/(\d{1,2}):(\d{2})|\s\d{1,2}\s/g);
  const ampm = category.match(/(a|p).m./g);
  let updatedDate = '';
  if (month) updatedDate += `${formatMonth(month)} `;
  if (day) updatedDate += `${day[0]} `;
  if (year) updatedDate += year[0];
  if (time) {
    updatedDate += ` ${time[0]} ${ampm[0]}`;
  }
  return updatedDate;
}

function formatMonth(month) {
  return month[0].slice(0, 3);
}

function getTooltipY(axes, tooltips, y) {
  if (axes.units) {
    return format.getDivisor(axes.units.divisor) * y / format.getDivisor(tooltips.divisor);
  }
  return y;
}

function getValue(y, isPercent, prefix, suffix, decimals, needsCommas) {
  return isPercent ?
    `${y.toFixed(1)}%`
    :
    formatY(y, decimals, needsCommas, prefix, suffix);
}

function formatY(y, decimals, needsCommas, prefix, suffix) {
  y = y.toFixed(decimals);

  if (!needsCommas) return y;

  const [dollars, cents] = y.split('.');
  const digitsArr = dollars.split('').reverse();
  const commasAdded = addCommas(digitsArr);
  let finalString = prefix + formatDollars(commasAdded);
  if (cents) finalString += `.${cents}`;
  return finalString + suffix;
}

function addCommas(digits) {
  return digits.reduce((str, digit, i) => str + checkComma(i) + digit, '');
}

function checkComma(i) {
  return i % 3 === 0 && i > 0 ? ',' : '';
}

function formatDollars(digits) {
  return digits.split('').reverse().join('');
}

function findIfReverse(categories) {
  let currentDateComparison = categories[0]
  if (isNaN(Date.parse(currentDateComparison))) {
    let currentDateComparisonLength = currentDateComparison.split(' ').length;
    if (currentDateComparisonLength === 2) {
      currentDateComparison = currentDateComparison.split(' ').join(' 1, ');
      currentDateComparison = currentDateComparison.replace('.', '');
    }
  }
  for (let f = 1; f < categories.length; f++) {
    let currentCategory = categories[f];
    if (isNaN(Date.parse(currentCategory))) {
      let currentIndexComparisonLength = categories[f].split(' ').length;
      if (currentIndexComparisonLength === 2) {
        currentCategory = currentCategory.split(' ').join(' 1, ');
        currentCategory = currentCategory.replace('.', '');
      }
    }
    if (isNaN(new Date(currentCategory))) {
      return false;
    }
    if (f === 1) {
      if (Date.parse(currentDateComparison) < Date.parse(currentCategory)) {
        return false;
      }
    } else {
      // currentDateComparison = categories[f - 1];
      if (Date.parse(currentDateComparison) < Date.parse(currentCategory)) {
        return false;
      }
      if (f === categories.length - 1 && Date.parse(currentDateComparison) > Date.parse(currentCategory)) {
        return true;
      }
      
    }
  }
}

function getDataPoint(series, context, isReverse) {
  if (context.x !== undefined) {
    for (let s = 0; s < series.length; s++) {
      for (let d = 0; d < series[s].data.length; d++) {
        if (series[s].data[d].x === context.x) {
          return { point: series[s].data[d], index: series[s].data[d].index };
        }
      }
    }
  }
  return {
    point: context.point,
    index: context.point.hasOwnProperty('index') ? context.point.index :
      context.point.x
  };
}

// NEVER CALLED
// function divideValue(value, divisorUnit) {
//   if (divisorUnit && divisorUnit !== 'none') {
//     const divisor = format.getDivisor(divisorUnit);
//     return +value / divisor;
//   }
//   return value;
// }

function space() {
  return '<span style="color: rgba(255, 255, 255, 0);">x</span>';
}


function formatStandard(options, context, formatter) {
  const isWSJWeight = options.product === 'wsj' ? 500 : 600;
  return [
    '<span>',
      formatSeries(options, context),
      '<span>',
        formatter.category,
      '</span>',
      space(),
      `<span style="font-weight: ${isWSJWeight};">`,
        formatter.value,
      '</span>',
    '</span>'
  ].join('');
}


function formatStackedPercent(data, options, context, formatter) {
  const isWSJWeight = options.product === 'wsj' ? 500 : 600;
  let total = 0;
  data.series.forEach(item => {
    total += item.data[formatter.index].y;
  });
  return [
    '<span style="',
    formatter.style,
    '">',
      formatSeries(options, context),
      '<span style="padding: 0 6px 0 0;">',
        formatter.category,
      '</span>',
      space(),
      `<span style="font-weight: ${isWSJWeight}; margin: 0 0 0 24px">`,
        formatter.value,
        ' (',
        Math.round(context.point.y / total * 100),
        '%)',
      '</span>',
    '</span>'
  ].join('');
}



function formatSeries(options, context) {
  if (options.legend.enabled && options.series.length > 1) {
    let css;
    let defaultCSS = [
      'padding: 0 6px 0 0;',
    ];

    if (options.product !== 'wsj') {
      css = defaultCSS.concat([
        'font-weight: 600;',
        `color: ${context.series.color};`
      ]).join('');
    }

    if (options.product === 'wsj') {
      css = defaultCSS.concat([
        'font-weight: 500;'
      ]).join('');
    }
    let nameArray = options.type === 'scatter' ? context.point.value.toString().split(' ') : context.series.name.toString().split(' ');
    if (nameArray[0] === '<br') {
      for (let i = 0; i < nameArray.length; i++) {
        if (nameArray[i] === '<br' || nameArray[i] === '/>') {
          nameArray.splice(i, 1);
          i--
        }
      }
    }
    nameArray = nameArray.join(' ');
    return [
      `<span style="${css}">`,
      nameArray,
      '</span>',
      space()
    ].join('');
  }
}


function getStyle(options) {
  let width = options.width > 300 ? '400px' : '250px';
  let data = {
    padding: '8px',
    width: width,
    fontSize: '14px',
    fontFamily: options.styles.font.normal,
    fontWeight: options.styles.weight.normal,
    fontStyle: 'normal',
    color: '#000000',
    whiteSpace: 'normal !important',
    overflow: 'auto'
  };

  if (__WSJ__) {
    if (options.product === 'wsj') {
      data.fontSize = '15px';
      data.lineHeight = '18px';
      data.fontWeight = 300;
    }
  }

  return data;
}



export function postRender(highchart, options) {
}



// WEBPACK FOOTER //
// ./src/chart/inner/tooltips.js